import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  DeviceThermostat,
  Water,
  Co2,
  WbSunny,
  Grass,
  Science,
  ElectricBolt,
  Refresh,
  FilterList,
  GetApp,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Sensor, SensorData } from '../types';
import { sensorApi } from '../services/api';

interface SensorReading {
  id: string;
  timestamp: string;
  sensorName: string;
  value: number;
  unit: string;
  status: 'normal' | 'warning' | 'critical';
  location: string;
}

interface SensorStats {
  id: string;
  name: string;
  currentValue: number;
  unit: string;
  trend: 'up' | 'down';
  trendValue: number;
  min: number;
  max: number;
  avg: number;
  icon: React.ReactNode;
  color: string;
  chartData: Array<{ time: string; value: number }>;
}

const SensorDataPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState('1h');
  const [selectedSensor, setSelectedSensor] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [sensorDataMap, setSensorDataMap] = useState<Map<number, SensorData[]>>(new Map());

  // 실제 센서 데이터에서 생성된 통계
  const [sensorStats, setSensorStats] = useState<SensorStats[]>([]);

  // 실제 센서 판독값 데이터
  const [sensorReadings, setSensorReadings] = useState<SensorReading[]>([]);

  // 센서 타입별 아이콘 매핑
  const getSensorIcon = (sensorType: string) => {
    switch (sensorType.toLowerCase()) {
      case 'temperature': return <DeviceThermostat />;
      case 'humidity': return <Water />;
      case 'co2': return <Co2 />;
      case 'light': return <WbSunny />;
      case 'soil_moisture': return <Grass />;
      case 'ph': return <Science />;
      case 'ec': return <ElectricBolt />;
      default: return <DeviceThermostat />;
    }
  };

  // 센서 타입별 색상 매핑
  const getSensorColor = (sensorType: string) => {
    switch (sensorType.toLowerCase()) {
      case 'temperature': return '#FF6B9D';
      case 'humidity': return '#2196F3';
      case 'co2': return '#FF9800';
      case 'light': return '#FFC107';
      case 'soil_moisture': return '#4CAF50';
      case 'ph': return '#9C27B0';
      case 'ec': return '#795548';
      default: return '#FF6B9D';
    }
  };

  // 센서 타입별 단위 매핑
  const getSensorUnit = (sensorType: string) => {
    switch (sensorType.toLowerCase()) {
      case 'temperature': return '°C';
      case 'humidity': return '%';
      case 'co2': return 'ppm';
      case 'light': return 'lux';
      case 'soil_moisture': return '%';
      case 'ph': return 'pH';
      case 'ec': return 'EC';
      default: return '';
    }
  };

  // 값에 따른 상태 판정
  const getValueStatus = (value: number, sensorType: string): 'normal' | 'warning' | 'critical' => {
    // 간단한 임계값 예시 (실제로는 센서별 설정된 임계값 사용)
    switch (sensorType.toLowerCase()) {
      case 'temperature':
        if (value < 18 || value > 28) return 'critical';
        if (value < 20 || value > 26) return 'warning';
        return 'normal';
      case 'humidity':
        if (value < 40 || value > 80) return 'critical';
        if (value < 50 || value > 70) return 'warning';
        return 'normal';
      case 'co2':
        if (value > 1000) return 'critical';
        if (value > 800) return 'warning';
        return 'normal';
      case 'ph':
        if (value < 5.5 || value > 8.5) return 'critical';
        if (value < 6.0 || value > 8.0) return 'warning';
        return 'normal';
      case 'ec':
        if (value < 0.5 || value > 3.0) return 'critical';
        if (value < 1.0 || value > 2.5) return 'warning';
        return 'normal';
      default:
        return 'normal';
    }
  };

  // 데이터 로드
  const loadSensorData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 센서 목록 조회
      const sensorsResponse = await sensorApi.getAll();
      setSensors(sensorsResponse.data);

      // 각 센서의 데이터 조회
      const sensorDataPromises = sensorsResponse.data.map(async (sensor) => {
        try {
          const dataResponse = await sensorApi.getData(sensor.id);
          return { sensorId: sensor.id, data: dataResponse.data };
        } catch (error) {
          console.error(`센서 ${sensor.id} 데이터 조회 실패:`, error);
          return { sensorId: sensor.id, data: [] };
        }
      });

      const sensorDataResults = await Promise.all(sensorDataPromises);
      const newSensorDataMap = new Map();
      sensorDataResults.forEach(({ sensorId, data }) => {
        newSensorDataMap.set(sensorId, data);
      });
      setSensorDataMap(newSensorDataMap);

      // 센서 통계 생성
      const stats: SensorStats[] = sensorsResponse.data.map((sensor) => {
        const data = newSensorDataMap.get(sensor.id) || [];
        const recentData = data.slice(-24); // 최근 24개 데이터
        const values = recentData.map((d: any) => d.value);

        const currentValue = values.length > 0 ? values[values.length - 1] : 0;
        const previousValue = values.length > 1 ? values[values.length - 2] : currentValue;
        const trendValue = currentValue - previousValue;
        const trend = trendValue >= 0 ? 'up' : 'down';

        const min = values.length > 0 ? Math.min(...values) : 0;
        const max = values.length > 0 ? Math.max(...values) : 0;
        const avg = values.length > 0 ? values.reduce((a: number, b: number) => a + b, 0) / values.length : 0;

        const chartData = recentData.map((item: any, index: number) => ({
          time: new Date(item.timestamp).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
          value: item.value,
        }));

        return {
          id: sensor.id.toString(),
          name: sensor.name,
          currentValue: Number(currentValue.toFixed(2)),
          unit: getSensorUnit(sensor.sensor_type),
          trend,
          trendValue: Number(Math.abs(trendValue).toFixed(2)),
          min: Number(min.toFixed(2)),
          max: Number(max.toFixed(2)),
          avg: Number(avg.toFixed(2)),
          icon: getSensorIcon(sensor.sensor_type),
          color: getSensorColor(sensor.sensor_type),
          chartData,
        };
      });
      setSensorStats(stats);

      // 센서 판독값 생성 (모든 센서의 최신 데이터)
      const readings: SensorReading[] = [];
      sensorsResponse.data.forEach((sensor) => {
        const data = newSensorDataMap.get(sensor.id) || [];
        const latestData = data[data.length - 1];
        if (latestData) {
          readings.push({
            id: latestData.id.toString(),
            timestamp: new Date(latestData.timestamp).toLocaleString('ko-KR'),
            sensorName: sensor.name,
            value: latestData.value,
            unit: latestData.unit || getSensorUnit(sensor.sensor_type),
            status: getValueStatus(latestData.value, sensor.sensor_type),
            location: sensor.location || '온실 A동',
          });
        }
      });
      setSensorReadings(readings.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));

    } catch (error) {
      console.error('센서 데이터 로드 실패:', error);
      setError('센서 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 데이터 새로고침
  const handleRefresh = () => {
    loadSensorData();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    loadSensorData();

    // 30초마다 데이터 자동 갱신
    const interval = setInterval(() => {
      loadSensorData();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'normal':
        return <Chip label="정상" color="success" size="small" />;
      case 'warning':
        return <Chip label="경고" color="warning" size="small" />;
      case 'critical':
        return <Chip label="위험" color="error" size="small" />;
      default:
        return <Chip label="알 수 없음" color="default" size="small" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>센서 데이터를 불러오는 중...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleRefresh}>
          다시 시도
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>시간 범위</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              label="시간 범위"
            >
              <MenuItem value="1h">1시간</MenuItem>
              <MenuItem value="6h">6시간</MenuItem>
              <MenuItem value="24h">24시간</MenuItem>
              <MenuItem value="7d">7일</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>센서 선택</InputLabel>
            <Select
              value={selectedSensor}
              onChange={(e) => setSelectedSensor(e.target.value)}
              label="센서 선택"
            >
              <MenuItem value="all">전체 센서</MenuItem>
              {sensors.map((sensor) => (
                <MenuItem key={sensor.id} value={sensor.id.toString()}>
                  {sensor.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton color="primary" onClick={handleRefresh}>
            <Refresh />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<FilterList />}>
            필터
          </Button>
          <Button variant="outlined" startIcon={<GetApp />}>
            데이터 내보내기
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="실시간 모니터링" />
          <Tab label="상세 데이터" />
          <Tab label="센서 상태" />
        </Tabs>
      </Box>

      {/* Tab Panel 0: Real-time Monitoring */}
      {tabValue === 0 && (
        <Box>
          {/* Sensor Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {sensorStats.map((sensor) => (
              <Grid item xs={12} sm={6} md={4} key={sensor.id}>
                <Card sx={{ borderRadius: 3, height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: sensor.color,
                          color: 'white',
                          mr: 2,
                        }}
                      >
                        {sensor.icon}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {sensor.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mr: 1 }}>
                            {sensor.currentValue} {sensor.unit}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {sensor.trend === 'up' ? (
                              <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                            ) : (
                              <TrendingDown sx={{ fontSize: 16, color: 'info.main' }} />
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                color: sensor.trend === 'up' ? 'success.main' : 'info.main',
                                ml: 0.5,
                              }}
                            >
                              {sensor.trend === 'up' ? '+' : ''}{sensor.trendValue}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Statistics */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          최소
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {sensor.min}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          평균
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {sensor.avg}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          최대
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {sensor.max}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Mini Chart */}
                    <Box sx={{ height: 100 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={sensor.chartData}>
                          <defs>
                            <linearGradient id={`gradient-${sensor.id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={sensor.color} stopOpacity={0.3} />
                              <stop offset="95%" stopColor={sensor.color} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="time" hide />
                          <YAxis hide />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke={sensor.color}
                            fillOpacity={1}
                            fill={`url(#gradient-${sensor.id})`}
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Tab Panel 1: Detailed Data */}
      {tabValue === 1 && (
        <Box>
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>시간</TableCell>
                  <TableCell>센서</TableCell>
                  <TableCell>값</TableCell>
                  <TableCell>단위</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell>위치</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sensorReadings.map((reading) => (
                  <TableRow key={reading.id}>
                    <TableCell>{reading.timestamp}</TableCell>
                    <TableCell>{reading.sensorName}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{reading.value}</TableCell>
                    <TableCell>{reading.unit}</TableCell>
                    <TableCell>{getStatusChip(reading.status)}</TableCell>
                    <TableCell>{reading.location}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Tab Panel 2: Sensor Status */}
      {tabValue === 2 && (
        <Box>
          <Grid container spacing={3}>
            {sensors.map((sensor) => (
              <Grid item xs={12} sm={6} md={4} key={sensor.id}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: sensor.is_active ? 'success.light' : 'grey.300',
                          color: 'white',
                          mr: 2,
                        }}
                      >
                        {getSensorIcon(sensor.sensor_type)}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {sensor.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {sensor.sensor_type}
                        </Typography>
                      </Box>
                      <Chip
                        label={sensor.is_active ? '활성' : '비활성'}
                        color={sensor.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        위치
                      </Typography>
                      <Typography variant="body2">
                        {sensor.location || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        생성일
                      </Typography>
                      <Typography variant="body2">
                        {new Date(sensor.created_at).toLocaleDateString('ko-KR')}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default SensorDataPage;